import React from 'react';
import styled from 'styled-components';

// Define types for the props
interface GenericButtonProps {
  label: string;  // Button label text
  onClick: () => void;  // Click handler function
  disabled?: boolean;  // Optional disabled state
  type?: 'primary' | 'secondary';  // Button type for styling (primary, secondary)
}

// Styled component for the button container
const ButtonWrapper = styled.div`
  width: 100%;  // Make the wrapper take up full width
  min-width: 200px;  // Set a minimum width for larger screens
  margin-bottom: 1rem;  // Space between buttons

  @media (max-width: 576px) {
    min-width: 100%;  // Full width on mobile devices
  }
`;

// Base styled component for the button (neutral style)
const BaseButton = styled.button<{ type: 'primary' | 'secondary'; disabled?: boolean }>`
  width: 100%;  // Make the button span full width
  padding: 12px 20px;  // Padding for consistency on mobile
  font-size: 1rem;  // Standard font size
  font-weight: 600;  // Make the text bold
  border-radius: 8px;  // Rounded corners
  border: 1px solid #ccc;  // Subtle border
  background-color: ${({ type, disabled }) => 
    disabled ? '#dcdcdc' : type === 'primary' ? '#4a90e2' : '#f0f0f0'};
  color: ${({ type, disabled }) => 
    disabled ? '#999' : type === 'primary' ? '#fff' : '#4a90e2'};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};  // Disable cursor if button is disabled
  box-shadow: none;  // Remove shadow for a more neutral design
  transition: background-color 0.3s ease, transform 0.3s ease;

  &:hover {
    background-color: ${({ type, disabled }) => 
      disabled ? '#dcdcdc' : type === 'primary' ? '#357ab7' : '#e0e0e0'};
    transform: ${({ disabled }) => (disabled ? 'none' : 'translateY(-2px)')};
  }

  &:active {
    background-color: ${({ type }) => (type === 'primary' ? '#285a8a' : '#e0e0e0')};
    transform: translateY(0);  // Remove the "lift" effect when clicked
  }

  &:focus {
    outline: none;  // Remove focus outline
  }
`;

const GenericButton: React.FC<GenericButtonProps> = ({ label, onClick, disabled = false, type = 'primary' }) => {
  return (
    <ButtonWrapper>
      <BaseButton 
        onClick={onClick}
        disabled={disabled}
        type={type}
      >
        {label}
      </BaseButton>
    </ButtonWrapper>
  );
};

export default GenericButton;
